// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Document Preview Panel (Panel 3)
// Full document administration: viewer, editor, workflow,
// version selector, tabs, nueva edición, discard changes
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef, useMemo, type CSSProperties } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

import { C, TRANSITIONS } from '../../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import {
  fetchDocumento,
  fetchVersiones,
  actualizarContenido,
  transicionarEstado,
  nuevaEdicion,
} from '../../../../shared/services/api';
import {
  PAGE,
  PAGE_HEADER,
  PAGE_FOOTER,
  generateViewerCSS,
  EDITOR_PARAGRAPH_OPTIONS,
} from '../../../../../config/documentStyles';
import type {
  Documento,
  DocumentVersion,
  EstadoDocumento,
  DocumentoResumen,
} from '../../../../../types';
import { exportViewerToPdf } from '../../../../../lib/exportPdf';
import { VersionsTab } from '../viewer/VersionsTab';
import { ReferencesTab } from '../viewer/ReferencesTab';
import { ChangelogModal } from '../viewer/ChangelogModal';
import { ArchivosTab } from '../viewer/ArchivosTab';
import { ContentPages } from '../viewer/ContentPages';
import { renderContentBlocks, ViewerStyles } from '../viewer';
import type { ContentBlock } from '../viewer';

// ─── Types ───

type PanelTab = 'contenido' | 'versiones' | 'referencias' | 'archivos';

interface DocumentPreviewPanelProps {
  documento: DocumentoResumen | null;
  onClose?: () => void;
  onExpandFullscreen?: () => void;
  /** When true, panel renders NO internal header (parent provides it) */
  externalHeader?: boolean;
  /** Controlled editing state from parent (used with externalHeader) */
  editingFromParent?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Ref to trigger export from parent */
  onExportRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  /** Expose panel state to parent for header rendering */
  onStateChange?: (state: PanelState) => void;
  /** Use ContentPages component for A4 paginated view (like DocumentViewerPage) */
  useContentPages?: boolean;
  /** Hide internal tab bar, zoom bar, and workflow — parent controls them */
  hideInternalControls?: boolean;
  /** Navigate to a document by código (for cross-reference links within the panel) */
  onNavigateDoc?: (codigo: string) => void;
}

/** State exposed to parent for header rendering */
export interface PanelState {
  versions: DocumentVersion[];
  selectedVersionIndex: number;
  activeTab: PanelTab;
  doc: Documento | null;
  isEditing: boolean;
  saving: boolean;
  actionLoading: boolean;
  canEdit: boolean;
  canStartNewEdition: boolean;
  lastSaved: Date | null;
  /** Workflow transitions available for the current state */
  transitions: { label: string; accion: string; color: string }[];
  /** Handlers for parent to call */
  handlers: PanelHandlers;
}

/** Handlers exposed to parent for header buttons */
export interface PanelHandlers {
  selectVersion: (index: number) => void;
  enterEditMode: () => void;
  exitEditMode: () => Promise<void>;
  discardChanges: () => Promise<void>;
  manualSave: () => Promise<void>;
  triggerTransition: (accion: string) => Promise<void>;
  openNewEdition: () => void;
  exportPdf: () => Promise<void>;
}

/** Imperative handle exposed to parent */
export interface PanelActions {
  selectVersion: (index: number) => void;
  setTab: (tab: PanelTab) => void;
  enterEditMode: () => void;
  exitEditMode: () => Promise<void>;
  discardChanges: () => Promise<void>;
  manualSave: () => Promise<void>;
  triggerTransition: (accion: string) => Promise<void>;
  openNewEdition: () => void;
  exportPdf: () => Promise<void>;
}

// ─── Auto-number headings in ProseMirror JSON ───

function numberHeadings(json: any): any {
  // Pass through without filtering — show all content as-is
  return json;
}

// ─── ProseMirror JSON → HTML renderer ───

function renderMarks(text: string, marks?: any[]): string {
  if (!marks || marks.length === 0) return escapeHtml(text);
  let html = escapeHtml(text);
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        html = `<strong>${html}</strong>`;
        break;
      case 'italic':
        html = `<em>${html}</em>`;
        break;
      case 'underline':
        html = `<u>${html}</u>`;
        break;
      case 'strike':
        html = `<s>${html}</s>`;
        break;
      case 'code':
        html = `<code>${html}</code>`;
        break;
      case 'textStyle': {
        const s: string[] = [];
        if (mark.attrs?.color) s.push(`color:${mark.attrs.color}`);
        if (mark.attrs?.fontSize) s.push(`font-size:${mark.attrs.fontSize}`);
        if (s.length) html = `<span style="${s.join(';')}">${html}</span>`;
        break;
      }
      case 'link': {
        const href = mark.attrs?.href ?? '#';
        if (href.startsWith('/documento/')) {
          html = `<a href="${escapeHtml(href)}" data-internal-link="true" style="color:#2F5496;text-decoration:underline;cursor:pointer">${html}</a>`;
        } else {
          html = `<a href="${escapeHtml(href)}" target="_blank" rel="noopener" style="color:#2F5496;text-decoration:underline">${html}</a>`;
        }
        break;
      }
    }
  }
  return html;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderNode(node: any): string {
  if (!node) return '';
  switch (node.type) {
    case 'text':
      return renderMarks(node.text ?? '', node.marks);
    case 'heading': {
      const level = Math.min(node.attrs?.level ?? 1, 4);
      const inner = (node.content ?? []).map(renderNode).join('');
      return `<h${level}>${inner}</h${level}>`;
    }
    case 'paragraph': {
      const inner = (node.content ?? []).map(renderNode).join('');
      const cls = node.attrs?.pStyle ? ` class="ps-${node.attrs.pStyle}"` : '';
      return `<p${cls}>${inner}</p>`;
    }
    case 'bulletList':
      return `<ul>${(node.content ?? []).map(renderNode).join('')}</ul>`;
    case 'orderedList':
      return `<ol start="${node.attrs?.start ?? 1}">${(node.content ?? []).map(renderNode).join('')}</ol>`;
    case 'listItem':
      return `<li>${(node.content ?? []).map(renderNode).join('')}</li>`;
    case 'table':
      return `<table class="dtbl"><tbody>${(node.content ?? []).map(renderNode).join('')}</tbody></table>`;
    case 'tableRow':
      return `<tr>${(node.content ?? []).map(renderNode).join('')}</tr>`;
    case 'tableHeader': {
      const inner = (node.content ?? []).map(renderNode).join('');
      const colspan = node.attrs?.colspan > 1 ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan > 1 ? ` rowspan="${node.attrs.rowspan}"` : '';
      const bgStyle = node.attrs?.backgroundColor
        ? ` style="background:${node.attrs.backgroundColor}"`
        : '';
      return `<th${colspan}${rowspan}${bgStyle}>${inner}</th>`;
    }
    case 'tableCell': {
      const inner = (node.content ?? []).map(renderNode).join('');
      const colspan = node.attrs?.colspan > 1 ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan > 1 ? ` rowspan="${node.attrs.rowspan}"` : '';
      const bgStyle = node.attrs?.backgroundColor
        ? ` style="background:${node.attrs.backgroundColor}"`
        : '';
      return `<td${colspan}${rowspan}${bgStyle}>${inner}</td>`;
    }
    case 'blockquote':
      return `<blockquote class="callout">${(node.content ?? []).map(renderNode).join('')}</blockquote>`;
    case 'horizontalRule':
      return '<hr/>';
    case 'hardBreak':
      return '<br/>';
    case 'codeBlock':
      return `<pre><code>${(node.content ?? []).map(renderNode).join('')}</code></pre>`;
    case 'image':
      return `<img src="${escapeHtml(node.attrs?.src ?? '')}" alt="${escapeHtml(node.attrs?.alt ?? '')}" style="max-width:100%"/>`;
    default:
      return node.content ? (node.content as any[]).map(renderNode).join('') : '';
  }
}

function renderContent(json: any): string {
  if (!json?.content) return '';
  return json.content.map((node: any) => renderNode(node)).join('');
}

// ─── Viewer CSS (cached) ───
const viewerCSS = generateViewerCSS();

// ─── Component ───

export function DocumentPreviewPanel({
  documento,
  onClose: _onClose,
  onExpandFullscreen,
  externalHeader = false,
  editingFromParent,
  onEditingChange,
  onExportRef,
  onStateChange,
  useContentPages: useContentPagesMode = false,
  hideInternalControls = false,
  onNavigateDoc,
}: DocumentPreviewPanelProps) {
  const { t } = useI18n();
  const documentoCodigo = documento?.codigo ?? null;

  // ─── Core state ───
  const [doc, setDoc] = useState<Documento | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PanelTab>('contenido');

  // ─── Editing state ───
  const [isEditingInternal, setIsEditingInternal] = useState(false);
  const isEditing = editingFromParent !== undefined ? editingFromParent : isEditingInternal;
  const setIsEditing = useCallback(
    (v: boolean) => {
      setIsEditingInternal(v);
      onEditingChange?.(v);
    },
    [onEditingChange],
  );

  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Workflow state ───
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Changelog modal ───
  const [changelogOpen, setChangelogOpen] = useState(false);

  // ─── Discard confirmation ───
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // ─── Zoom ───
  const [zoom, setZoom] = useState(100);
  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 10, 200)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - 10, 30)), []);

  // ─── Content blocks for ContentPages mode ───
  const [contentBlocksA4, setContentBlocksA4] = useState<ContentBlock[]>([]);

  // ─── TipTap editor ───
  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: null,
    editable: false,
  });

  // ─── Derived state ───
  const selectedVersion = versions[selectedVersionIndex] ?? null;
  // 0661.5 — estado reflejado en cover/StatusBadge debe corresponder a la
  // VERSIÓN VISIBLE (selectedVersion), no a doc.estado. Sin esto, ver una
  // versión Ed.N EN_REVISION mostraba "Aprobado" en la portada porque
  // doc.estado seguía siendo APROBADO de la versión vigente publicada.
  // Inconsistencia detectada por el CEO entre header (EN REVISION) y
  // portada del documento (Aprobado).
  const estado = (selectedVersion?.estado ?? doc?.estado ?? 'BORRADOR') as EstadoDocumento;
  const transitions = TRANSITIONS[estado] ?? [];
  const borradorVersion = versions.find(
    (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
  );
  const canEdit = !!borradorVersion;
  const canStartNewEdition =
    !borradorVersion &&
    (estado === 'PUBLICADO' ||
      estado === 'APROBADO' ||
      doc?.estado === 'PUBLICADO' ||
      doc?.estado === 'APROBADO');

  // ─── Toast helper ───
  const showToast = useCallback((msg: string, duration = 3000) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  // ─── Stable handlers ref for parent (updated after handlers are defined) ───
  const handlersRef = useRef<PanelHandlers>({
    selectVersion: () => {},
    enterEditMode: () => {},
    exitEditMode: async () => {},
    discardChanges: async () => {},
    manualSave: async () => {},
    triggerTransition: async () => {},
    openNewEdition: () => {},
    exportPdf: async () => {},
  });

  // ─── Placeholder: Notify parent effect is defined AFTER all handlers ───
  // (see below, after handleExportPdf)

  // ─── Sync editing state from parent ───
  useEffect(() => {
    if (editingFromParent !== undefined && editor) {
      editor.setEditable(editingFromParent);
      if (!editingFromParent && selectedVersion) {
        actualizarContenido(selectedVersion.id, editor.getJSON()).catch(() => {});
      }
    }
  }, [editingFromParent]);

  // ─── Fetch data ───
  useEffect(() => {
    if (!documentoCodigo) {
      setDoc(null);
      setVersions([]);
      setSelectedVersionIndex(0);
      setError(null);
      setIsEditing(false);
      setActiveTab('contenido');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setIsEditing(false);
    setActiveTab('contenido');

    Promise.all([fetchDocumento(documentoCodigo), fetchVersiones(documentoCodigo)])
      .then(([docRes, verRes]) => {
        if (cancelled) return;
        setDoc(docRes.data);
        const vers = verRes.data ?? [];
        setVersions(vers);
        setSelectedVersionIndex(0);
        const latest = vers[0] ?? null;
        if (editor && latest?.contenidoJson) {
          editor.commands.setContent(latest.contenidoJson);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? t.common.errorCargarDocumento);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentoCodigo, editor]);

  // ─── Update editor content when selected version changes (not editing) ───
  useEffect(() => {
    if (!versions.length || isEditing) return;
    const version = versions[selectedVersionIndex] ?? null;
    if (editor && version?.contenidoJson) {
      editor.commands.setContent(version.contenidoJson);
    }
  }, [selectedVersionIndex, versions, isEditing, editor]);

  // ─── Auto-save (every 30s in edit mode) ───
  const doSave = useCallback(async () => {
    if (!editor || !selectedVersion) return;
    const editVersion = borradorVersion ?? selectedVersion;
    setSaving(true);
    try {
      await actualizarContenido(editVersion.id, editor.getJSON());
      setLastSaved(new Date());
    } catch {
      // silently fail auto-save
    } finally {
      setSaving(false);
    }
  }, [editor, selectedVersion, borradorVersion]);

  useEffect(() => {
    if (isEditing) {
      autoSaveRef.current = setInterval(doSave, 30000);
    } else if (autoSaveRef.current) {
      clearInterval(autoSaveRef.current);
      autoSaveRef.current = null;
    }
    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [isEditing, doSave]);

  // ─── Edit mode toggle ───
  const enterEditMode = useCallback(() => {
    if (!editor || !versions.length) return;
    // Find borrador/revision version and switch to it
    const editVersionIdx = versions.findIndex(
      (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
    );
    const editVersion = editVersionIdx >= 0 ? versions[editVersionIdx] : versions[0];
    if (editVersionIdx >= 0) setSelectedVersionIndex(editVersionIdx);
    editor.setEditable(true);
    editor.commands.setContent(editVersion.contenidoJson);
    setIsEditing(true);
    setActiveTab('contenido');
  }, [editor, versions, setIsEditing]);

  const exitEditMode = useCallback(async () => {
    if (!editor) return;
    // Save before exiting
    const editVersion = borradorVersion ?? selectedVersion;
    if (editVersion) {
      setSaving(true);
      try {
        await actualizarContenido(editVersion.id, editor.getJSON());
        setLastSaved(new Date());
      } catch {
        /* ignore */
      } finally {
        setSaving(false);
      }
    }
    editor.setEditable(false);
    setIsEditing(false);
    // Refresh versions
    if (documentoCodigo) {
      try {
        const verRes = await fetchVersiones(documentoCodigo);
        setVersions(verRes.data);
      } catch {
        /* ignore */
      }
    }
  }, [editor, borradorVersion, selectedVersion, documentoCodigo, setIsEditing]);

  const handleManualSave = useCallback(async () => {
    if (!editor) return;
    const editVersion = borradorVersion ?? selectedVersion;
    if (!editVersion) return;
    setSaving(true);
    try {
      await actualizarContenido(editVersion.id, editor.getJSON());
      setLastSaved(new Date());
      showToast(t.common.documentSaved);
    } catch (err: any) {
      showToast(`${t.common.errorSaving}${err.message ?? t.common.errorDesconocido}`, 4000);
    } finally {
      setSaving(false);
    }
  }, [editor, borradorVersion, selectedVersion, showToast]);

  // ─── Discard changes ───
  const discardChanges = useCallback(async () => {
    if (!documentoCodigo || !editor) return;
    try {
      const verRes = await fetchVersiones(documentoCodigo);
      setVersions(verRes.data);
      const editVersion =
        verRes.data.find(
          (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
        ) ?? verRes.data[0];
      if (editVersion?.contenidoJson) {
        editor.commands.setContent(editVersion.contenidoJson);
      }
      editor.setEditable(false);
      setIsEditing(false);
      setDiscardConfirmOpen(false);
      showToast(t.common.changesDiscarded);
    } catch (err: any) {
      showToast(err.message || t.common.errorDescartar, 4000);
    }
  }, [documentoCodigo, editor, setIsEditing, showToast]);

  // ─── Workflow transitions ───
  const handleTransition = useCallback(
    async (accion: string) => {
      if (!documentoCodigo) return;
      // Use the CURRENT selectedVersion from versions array (not stale closure)
      const currentVersion = versions[selectedVersionIndex];
      if (!currentVersion) return;

      // Validate action is still valid for current state
      const validActions = TRANSITIONS[currentVersion.estado as string];
      if (!validActions?.some((tr) => tr.accion === accion)) {
        showToast(t.common.accionNoValida(accion, currentVersion.estado as string), 4000);
        return;
      }

      setActionLoading(true);
      try {
        await transicionarEstado(currentVersion.id, accion);
        const [docRes, verRes] = await Promise.all([
          fetchDocumento(documentoCodigo),
          fetchVersiones(documentoCodigo),
        ]);
        setDoc(docRes.data);
        setVersions(verRes.data);
        setSelectedVersionIndex(0);
        if (editor && verRes.data[0]?.contenidoJson) {
          editor.commands.setContent(verRes.data[0].contenidoJson);
        }
        showToast(t.common.statusUpdated);
      } catch (err: any) {
        // On error, refresh data to get correct state
        try {
          const [docRes, verRes] = await Promise.all([
            fetchDocumento(documentoCodigo),
            fetchVersiones(documentoCodigo),
          ]);
          setDoc(docRes.data);
          setVersions(verRes.data);
          setSelectedVersionIndex(0);
        } catch {
          /* ignore refresh errors */
        }
        showToast(err.message || t.common.errorTransicion, 4000);
      } finally {
        setActionLoading(false);
      }
    },
    [versions, selectedVersionIndex, documentoCodigo, editor, showToast],
  );

  // ─── New Edition ───
  const handleNewEdition = useCallback(
    async (descripcion: string) => {
      if (!documentoCodigo) return;
      setActionLoading(true);
      try {
        await nuevaEdicion(documentoCodigo, descripcion);
        setChangelogOpen(false);
        const [docRes, verRes] = await Promise.all([
          fetchDocumento(documentoCodigo),
          fetchVersiones(documentoCodigo),
        ]);
        setDoc(docRes.data);
        setVersions(verRes.data);
        setSelectedVersionIndex(0);
        if (editor && verRes.data.length) {
          editor.setEditable(true);
          editor.commands.setContent(verRes.data[0].contenidoJson);
          setIsEditing(true);
          setActiveTab('contenido');
        }
        showToast(t.common.newEditionCreated);
      } catch (err: any) {
        showToast(err.message || t.common.errorNewEdition, 4000);
      } finally {
        setActionLoading(false);
      }
    },
    [documentoCodigo, editor, setIsEditing, showToast],
  );

  // ─── Version selection handler ───
  const handleSelectVersion = useCallback(
    (index: number) => {
      if (isEditing) return; // Cannot switch versions while editing
      setSelectedVersionIndex(index);
      setActiveTab('contenido');
    },
    [isEditing],
  );

  // ─── Style selector handler ───
  const handleStyleChange = useCallback(
    (value: string) => {
      if (!editor) return;
      switch (value) {
        case 'h1':
          editor.chain().focus().toggleHeading({ level: 1 }).run();
          break;
        case 'h2':
          editor.chain().focus().toggleHeading({ level: 2 }).run();
          break;
        case 'h3':
          editor.chain().focus().toggleHeading({ level: 3 }).run();
          break;
        case 'h4':
          editor.chain().focus().toggleHeading({ level: 4 }).run();
          break;
        case 'p':
        case 'p-bold':
          editor.chain().focus().setParagraph().run();
          break;
        case 'list-bullet':
        case 'list-bullet-bold':
          editor.chain().focus().toggleBulletList().run();
          break;
        case 'list-num':
          editor.chain().focus().toggleOrderedList().run();
          break;
      }
    },
    [editor],
  );

  // ─── Rendered HTML for read mode ───
  const contentHtml = useMemo(() => {
    if (!selectedVersion?.contenidoJson) return '';
    const numbered = numberHeadings(selectedVersion.contenidoJson);
    return renderContent(numbered);
  }, [selectedVersion?.contenidoJson]);

  // ─── ContentPages blocks (A4 paginated mode) ───
  useEffect(() => {
    if (!useContentPagesMode || !selectedVersion?.contenidoJson) {
      setContentBlocksA4([]);
      return;
    }
    const numbered = numberHeadings(selectedVersion.contenidoJson);
    setContentBlocksA4(renderContentBlocks(numbered));
  }, [useContentPagesMode, selectedVersion?.contenidoJson]);

  // ─── Intercept clicks on internal cross-reference links ───
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[data-internal-link]') as HTMLAnchorElement | null;
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        if (href?.startsWith('/documento/')) {
          const codigo = decodeURIComponent(href.replace('/documento/', '').split('/')[0]);
          onNavigateDoc?.(codigo);
        }
      }
    };
    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [contentHtml, onNavigateDoc]);

  // ─── Labels ───
  const editionLabel = selectedVersion
    ? `Ed. ${String(selectedVersion.numeroEdicion).padStart(2, '0')}`
    : '';
  const dateLabel = selectedVersion?.actualizadoEn
    ? new Date(selectedVersion.actualizadoEn).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  // ─── Export PDF ───
  const handleExportPdf = useCallback(async () => {
    if (!doc || !panelRef.current) return;
    const filename = `${doc.codigo} ${doc.nombre}.pdf`;
    await exportViewerToPdf(panelRef.current, filename);
  }, [doc]);

  // ─── Expose export function to parent ───
  useEffect(() => {
    if (onExportRef) onExportRef.current = handleExportPdf;
    return () => {
      if (onExportRef) onExportRef.current = null;
    };
  }, [handleExportPdf, onExportRef]);

  // ─── Notify parent of state changes (after all handlers are defined) ───
  useEffect(() => {
    // Update handlers ref with latest closures
    handlersRef.current = {
      selectVersion: handleSelectVersion,
      enterEditMode,
      exitEditMode,
      discardChanges,
      manualSave: handleManualSave,
      triggerTransition: handleTransition,
      openNewEdition: () => setChangelogOpen(true),
      exportPdf: handleExportPdf,
    };

    onStateChange?.({
      versions,
      selectedVersionIndex,
      activeTab,
      doc,
      isEditing,
      saving,
      actionLoading,
      canEdit,
      canStartNewEdition,
      lastSaved,
      transitions,
      handlers: handlersRef.current,
    });
  }, [
    versions,
    selectedVersionIndex,
    activeTab,
    doc,
    isEditing,
    saving,
    actionLoading,
    canEdit,
    canStartNewEdition,
    lastSaved,
    transitions,
    handleSelectVersion,
    enterEditMode,
    exitEditMode,
    discardChanges,
    handleManualSave,
    handleTransition,
    handleExportPdf,
  ]);

  // ─── Reference counts ───
  const refCount = (doc?.referenciasSalida?.length ?? 0) + (doc?.referenciasEntrada?.length ?? 0);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  // ─── Placeholder (no document selected) ───
  if (!documentoCodigo) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <Ic d={ICON_PATHS.doc} s={48} c={C.textMut} />
          <p style={styles.placeholderText}>{t.common.selectDocFromList}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <p style={styles.placeholderText}>{t.common.loadingDocument}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <p style={{ ...styles.placeholderText, color: '#DC2626' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <p style={styles.placeholderText}>{t.common.documentNotFound}</p>
        </div>
      </div>
    );
  }

  const titleText = `${doc.codigo} ${doc.nombre}`;

  return (
    <div ref={panelRef} style={styles.container}>
      {/* Injected CSS */}
      <style>{viewerCSS}</style>
      <style>{`
        .dpanel-btn:hover { background: ${C.borderAlt} !important; }
        .dpanel-btn-primary:hover { opacity: 0.9 !important; }
        .dpanel-toolbar-btn:hover { background: ${C.border} !important; }
        .dpanel-toolbar-btn.active { background: ${C.border}; color: ${C.text}; }
        .dpanel-tab:hover { color: ${C.azul2} !important; }
        .dpanel-wf-btn:hover { opacity: 0.85; }
        .dpanel-version-opt:hover { background: ${C.borderAlt}; }
      `}</style>

      {/* ─── Header (only when NOT using external header) ─── */}
      {!externalHeader && (
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerTitle} title={titleText}>
              {titleText}
            </span>
            <StatusBadge status={estado} small />
            {selectedVersion && <span style={styles.editionBadge}>{editionLabel}</span>}
          </div>
          <div style={styles.headerRight}>
            {/* Version selector */}
            {versions.length > 1 && !isEditing && (
              <select
                value={selectedVersionIndex}
                onChange={(e) => handleSelectVersion(Number(e.target.value))}
                style={styles.versionSelect}
              >
                {versions.map((v, i) => (
                  <option key={v.id} value={i}>
                    Ed. {String(v.numeroEdicion).padStart(2, '0')} (
                    {v.id === (doc as any)?.versionVigenteId
                      ? ((t.status as Record<string, string>)[doc.estado] ?? doc.estado)
                      : ((t.status as Record<string, string>)[v.estado] ??
                        (t.versionStatus as Record<string, string>)[v.estado] ??
                        v.estado)}
                    )
                  </option>
                ))}
              </select>
            )}
            {isEditing ? (
              <>
                <button
                  className="dpanel-btn-primary"
                  style={styles.btnPrimary}
                  onClick={handleManualSave}
                  disabled={saving}
                >
                  <Ic d={ICON_PATHS.save} s={14} c="#fff" />
                  {saving ? t.common.guardando : t.common.guardar}
                </button>
                <button
                  className="dpanel-btn"
                  style={styles.btn}
                  onClick={() => setDiscardConfirmOpen(true)}
                >
                  {t.common.discard}
                </button>
                <button className="dpanel-btn" style={styles.btn} onClick={exitEditMode}>
                  {t.common.closeEditor}
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <button className="dpanel-btn" style={styles.btn} onClick={enterEditMode}>
                    <Ic d={ICON_PATHS.edit} s={14} c={C.textSec} />
                    {t.common.editar}
                  </button>
                )}
                {canStartNewEdition && (
                  <button
                    className="dpanel-btn"
                    style={styles.btn}
                    onClick={() => setChangelogOpen(true)}
                  >
                    <Ic d={ICON_PATHS.plus} s={14} c={C.textSec} />
                    {t.common.newEditionBtn}
                  </button>
                )}
              </>
            )}
            <button className="dpanel-btn" style={styles.btn} onClick={handleExportPdf}>
              <Ic d={ICON_PATHS.download} s={14} c={C.textSec} />
              PDF
            </button>
            {onExpandFullscreen && (
              <button className="dpanel-btn" style={styles.btn} onClick={onExpandFullscreen}>
                ⛶
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Auto-save indicator ─── */}
      {isEditing && lastSaved && (
        <div style={styles.autoSaveBar}>
          {t.common.autoSave} {lastSaved.toLocaleTimeString('es-ES')}
        </div>
      )}

      {/* ─── Toolbar (edit mode) ─── */}
      {isEditing && editor && (
        <div style={styles.toolbar}>
          <select
            style={styles.styleSelect}
            onChange={(e) => handleStyleChange(e.target.value)}
            value=""
          >
            <option value="" disabled>
              {t.common.style}
            </option>
            {EDITOR_PARAGRAPH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div style={styles.toolbarSep} />
          <button
            className={`dpanel-toolbar-btn${editor.isActive('bold') ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title={t.common.negrita}
          >
            <Ic d={ICON_PATHS.bold} s={16} />
          </button>
          <button
            className={`dpanel-toolbar-btn${editor.isActive('italic') ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title={t.common.cursiva}
          >
            <Ic d={ICON_PATHS.italic} s={16} />
          </button>
          <div style={styles.toolbarSep} />
          <button
            className={`dpanel-toolbar-btn${editor.isActive('heading', { level: 1 }) ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            title={t.common.titulo1}
          >
            H1
          </button>
          <button
            className={`dpanel-toolbar-btn${editor.isActive('heading', { level: 2 }) ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title={t.common.titulo2}
          >
            H2
          </button>
          <button
            className={`dpanel-toolbar-btn${editor.isActive('heading', { level: 3 }) ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            title={t.common.titulo3}
          >
            H3
          </button>
          <div style={styles.toolbarSep} />
          <button
            className={`dpanel-toolbar-btn${editor.isActive('bulletList') ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title={t.common.listaConVinetas}
          >
            <Ic d={ICON_PATHS.listBul} s={16} />
          </button>
          <button
            className={`dpanel-toolbar-btn${editor.isActive('orderedList') ? ' active' : ''}`}
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title={t.common.listaNumerada}
          >
            <Ic d={ICON_PATHS.listNum} s={16} />
          </button>
          <div style={styles.toolbarSep} />
          <button
            className="dpanel-toolbar-btn"
            style={styles.toolbarBtn}
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
            title={t.common.insertarTabla}
          >
            <Ic d={ICON_PATHS.table} s={16} />
          </button>
          <div style={styles.toolbarSep} />
          <button
            className="dpanel-toolbar-btn"
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title={t.common.deshacer}
          >
            <Ic d={ICON_PATHS.undo} s={16} />
          </button>
          <button
            className="dpanel-toolbar-btn"
            style={styles.toolbarBtn}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title={t.common.rehacer}
          >
            <Ic d={ICON_PATHS.redo} s={16} />
          </button>
        </div>
      )}

      {/* ─── Tab bar ─── */}
      {!hideInternalControls && (
        <div style={styles.tabBar}>
          {(['contenido', 'versiones', 'referencias', 'archivos'] as PanelTab[]).map((tab) => {
            const isActive = activeTab === tab;
            const labels: Record<PanelTab, string> = {
              contenido: t.viewer.tabs.content,
              versiones: `${t.viewer.tabs.versions} (${versions.length})`,
              referencias: `${t.viewer.tabs.references}${refCount > 0 ? ` (${refCount})` : ''}`,
              archivos: t.archivos.tabArchivos,
            };
            return (
              <button
                key={tab}
                className="dpanel-tab"
                onClick={() => setActiveTab(tab)}
                style={{
                  ...styles.tabBtn,
                  color: isActive ? C.azul2 : C.textSec,
                  borderBottom: isActive ? `2px solid ${C.azul2}` : '2px solid transparent',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>
      )}

      {/* ─── CONTENIDO TAB ─── */}
      {activeTab === 'contenido' && (
        <>
          {/* Zoom controls */}
          {!hideInternalControls && (
            <div style={styles.zoomBar}>
              <button onClick={zoomOut} style={styles.zoomBtn}>
                {'\u2212'}
              </button>
              <span style={styles.zoomLabel}>{zoom}%</span>
              <button onClick={zoomIn} style={styles.zoomBtn}>
                +
              </button>
            </div>
          )}

          {/* Content area — ContentPages A4 mode */}
          {useContentPagesMode && !isEditing ? (
            <div style={{ flex: 1, overflow: 'auto' }}>
              <ViewerStyles />
              <ContentPages
                contentBlocks={contentBlocksA4}
                editing={false}
                editor={null}
                zoom={zoom}
                coverPageProps={{
                  doc: doc,
                  estado,
                  latestVersion: selectedVersion,
                  renderPageFooter: (pageNum: number, sectionTitle: string) => (
                    <div
                      style={{
                        padding: `8px ${PAGE.padSide}px 16px`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: PAGE_FOOTER.fontSize,
                        color: PAGE_FOOTER.color,
                        fontFamily: PAGE_FOOTER.fontFamily,
                        borderTop: PAGE_FOOTER.borderTop,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {sectionTitle || doc.codigo}
                      </span>
                      <span>
                        {t.common.page} {pageNum}
                      </span>
                    </div>
                  ),
                  totalPages: 999,
                }}
                renderPageHeader={() => (
                  <div
                    style={{
                      padding: `${PAGE.padTop * 0.27}px ${PAGE.padSide}px 8px`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: PAGE_HEADER.borderBottom,
                      fontFamily: PAGE_HEADER.fontFamily,
                      fontSize: PAGE_HEADER.fontSize,
                      flexShrink: 0,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img
                        src={PAGE_HEADER.logoSrc}
                        alt=""
                        style={{ height: PAGE_HEADER.logoHeight, width: 'auto' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <span style={{ color: PAGE_HEADER.mutedColor }}>{doc.empresa ?? ''}</span>
                    </div>
                    <span
                      style={{
                        color: PAGE_HEADER.accentColor,
                        fontWeight: 600,
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                    >
                      {doc.nombre}
                    </span>
                    <div style={{ textAlign: 'right', color: PAGE_HEADER.mutedColor }}>
                      <div>{dateLabel}</div>
                      <div>{editionLabel}</div>
                    </div>
                  </div>
                )}
                renderPageFooter={(pageNum: number, sectionTitle: string) => (
                  <div
                    style={{
                      padding: `8px ${PAGE.padSide}px 16px`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: PAGE_FOOTER.fontSize,
                      color: PAGE_FOOTER.color,
                      fontFamily: PAGE_FOOTER.fontFamily,
                      borderTop: PAGE_FOOTER.borderTop,
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {sectionTitle || doc.codigo}
                    </span>
                    <span>
                      {t.common.page} {pageNum}
                    </span>
                  </div>
                )}
              />
            </div>
          ) : (
            /* Content area — simple scrollable mode (original) */
            <div style={styles.contentScroll}>
              <div
                data-a4-page
                style={{
                  ...styles.a4Container,
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                  marginBottom: zoom < 100 ? -(PAGE.height * (1 - zoom / 100)) : 0,
                }}
              >
                {/* A4 Page Header (read mode only) */}
                {!isEditing && (
                  <div style={styles.pageHeader}>
                    <div style={styles.pageHeaderLeft}>
                      <img
                        src={PAGE_HEADER.logoSrc}
                        alt="V\u00B7IANet"
                        style={styles.pageHeaderLogo}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <span style={styles.pageHeaderDocName}>{doc.nombre}</span>
                    </div>
                    <div style={styles.pageHeaderRight}>
                      <span style={styles.pageHeaderMeta}>{editionLabel}</span>
                      {dateLabel && (
                        <>
                          <span style={styles.pageHeaderSep}>|</span>
                          <span style={styles.pageHeaderMeta}>{dateLabel}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Document content */}
                {isEditing && editor ? (
                  <div className="viewer-content">
                    <EditorContent editor={editor} />
                  </div>
                ) : (
                  <div
                    ref={contentRef}
                    className="viewer-content"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                )}
                {!contentHtml && !isEditing && (
                  <p style={styles.placeholderText}>{t.common.noTieneContenido}</p>
                )}

                {/* A4 Page Footer (read mode only) */}
                {!isEditing && (
                  <div style={styles.pageFooter}>
                    <span style={styles.pageFooterText}>
                      {doc.codigo} {doc.nombre}
                    </span>
                    <span style={styles.pageFooterText}>{t.common.page} 1 / 1</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── VERSIONES TAB ─── */}
      {activeTab === 'versiones' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <VersionsTab
            versions={versions}
            selectedVersionIndex={selectedVersionIndex}
            onSelectVersion={handleSelectVersion}
            versionVigenteId={(doc as any)?.versionVigenteId}
          />
        </div>
      )}

      {/* ─── REFERENCIAS TAB ─── */}
      {activeTab === 'referencias' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ReferencesTab
            referenciasSalida={doc.referenciasSalida ?? []}
            referenciasEntrada={doc.referenciasEntrada ?? []}
          />
        </div>
      )}

      {/* ─── ARCHIVOS TAB ─── */}
      {activeTab === 'archivos' && doc && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ArchivosTab codigoDocumento={doc.codigo} readOnly={estado !== 'BORRADOR'} />
        </div>
      )}

      {/* ─── WORKFLOW BAR (sticky bottom — only when NOT using external header) ─── */}
      {!externalHeader && transitions.length > 0 && !isEditing && activeTab === 'contenido' && (
        <div style={styles.workflowBar}>
          <StatusBadge status={estado} small />
          <span style={styles.workflowLabel}>{doc.codigo}</span>
          <div style={{ flex: 1 }} />
          {transitions.map((tr) => (
            <button
              key={tr.accion}
              className="dpanel-wf-btn"
              onClick={() => handleTransition(tr.accion)}
              disabled={actionLoading}
              style={{
                ...styles.workflowBtn,
                borderColor: tr.color,
                color: tr.color,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = tr.color;
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = tr.color;
              }}
            >
              {(
                {
                  ENVIAR: t.workflow.sendToReview,
                  APROBAR: t.workflow.approve,
                  RECHAZAR: t.workflow.reject,
                  PUBLICAR: t.workflow.publish,
                } as Record<string, string>
              )[tr.accion] ?? tr.label}
            </button>
          ))}
          {canStartNewEdition && (
            <button
              className="dpanel-wf-btn"
              onClick={() => setChangelogOpen(true)}
              disabled={actionLoading}
              style={{
                ...styles.workflowBtn,
                borderColor: C.azul2,
                color: C.azul2,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.azul2;
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = C.azul2;
              }}
            >
              {t.common.nuevaEdicionBtn}
            </button>
          )}
        </div>
      )}

      {/* ─── CHANGELOG MODAL ─── */}
      <ChangelogModal
        open={changelogOpen}
        loading={actionLoading}
        onConfirm={handleNewEdition}
        onCancel={() => setChangelogOpen(false)}
      />

      {/* ─── DISCARD CONFIRMATION DIALOG ─── */}
      {discardConfirmOpen && (
        <div onClick={() => setDiscardConfirmOpen(false)} style={styles.modalOverlay}>
          <div onClick={(e) => e.stopPropagation()} style={styles.discardDialog}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: C.azul1 }}>
              {t.common.descartarCambiosDialogTitle}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.textSec }}>
              {t.common.descartarCambiosDialogDesc}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDiscardConfirmOpen(false)} style={styles.btn}>
                {t.common.cancelar}
              </button>
              <button
                onClick={discardChanges}
                style={{
                  ...styles.btnPrimary,
                  backgroundColor: '#DC2626',
                }}
              >
                {t.common.discard}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TOAST ─── */}
      {toast && (
        <div style={styles.toast}>
          <Ic d={ICON_PATHS.check} s={14} c="#fff" />
          {toast}
        </div>
      )}
    </div>
  );
}

// ─── Inline styles ───

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: C.pageBg,
    position: 'relative',
  },

  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 12,
    padding: 32,
  },

  placeholderText: {
    color: C.textMut,
    fontSize: 14,
    textAlign: 'center' as const,
    margin: 0,
    lineHeight: 1.5,
  },

  // ─── Header ───
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    gap: 8,
    flexShrink: 0,
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flex: 1,
  },

  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },

  editionBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: C.textSec,
    padding: '1px 5px',
    borderRadius: 3,
    backgroundColor: C.borderAlt,
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    flexWrap: 'wrap' as const,
  },

  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: C.textSec,
    backgroundColor: 'transparent',
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'background 0.15s',
  },

  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#FFFFFF',
    backgroundColor: C.azul2,
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'opacity 0.15s',
  },

  // ─── Version selector ───
  versionSelect: {
    padding: '3px 6px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    backgroundColor: C.surface,
    color: C.text,
    cursor: 'pointer',
    maxWidth: 180,
  },

  // ─── Auto-save bar ───
  autoSaveBar: {
    fontSize: 10,
    color: C.textMut,
    padding: '3px 12px',
    backgroundColor: C.borderAlt,
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
  },

  // ─── Tab bar ───
  tabBar: {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
    gap: 0,
  },

  tabBtn: {
    padding: '6px 14px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'color 0.15s',
  },

  // ─── Toolbar ───
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    flexWrap: 'wrap' as const,
    padding: '6px 10px',
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    position: 'sticky' as const,
    top: 0,
    zIndex: 10,
    flexShrink: 0,
  },

  toolbarBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 5,
    borderRadius: 3,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: C.textSec,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'inherit',
    lineHeight: 1,
    transition: 'background 0.1s',
  },

  toolbarSep: {
    width: 1,
    height: 18,
    backgroundColor: C.border,
    margin: '0 3px',
    flexShrink: 0,
  },

  styleSelect: {
    padding: '3px 6px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    backgroundColor: C.surface,
    color: C.text,
    cursor: 'pointer',
    minWidth: 120,
  },

  // ─── Zoom bar ───
  zoomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '4px 12px',
    flexShrink: 0,
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
  },

  zoomBtn: {
    width: 24,
    height: 24,
    borderRadius: 5,
    border: `1px solid ${C.border}`,
    background: C.surface,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: C.textSec,
    fontFamily: 'inherit',
    padding: 0,
  },

  zoomLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: C.textSec,
    minWidth: 36,
    textAlign: 'center' as const,
  },

  // ─── Content scroll area ───
  contentScroll: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '20px 12px',
    backgroundColor: C.pageBg,
  },

  // ─── A4 container ───
  a4Container: {
    background: '#FFFFFF',
    maxWidth: PAGE.width,
    margin: '0 auto',
    padding: `${PAGE.padTop}px ${PAGE.padSide}px ${PAGE.padBottom}px`,
    boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
    borderRadius: 2,
    minHeight: PAGE.height,
    position: 'relative' as const,
  },

  // ─── A4 Page Header ───
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
    marginBottom: 16,
    borderBottom: PAGE_HEADER.borderBottom,
    fontFamily: PAGE_HEADER.fontFamily,
    fontSize: PAGE_HEADER.fontSize,
  },
  pageHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 },
  pageHeaderLogo: { height: PAGE_HEADER.logoHeight, width: 'auto', flexShrink: 0 },
  pageHeaderDocName: {
    color: PAGE_HEADER.accentColor,
    fontWeight: 600,
    fontSize: PAGE_HEADER.fontSize,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  pageHeaderRight: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  pageHeaderMeta: {
    color: PAGE_HEADER.mutedColor,
    fontSize: PAGE_HEADER.fontSize,
    fontWeight: 400,
    whiteSpace: 'nowrap' as const,
  },
  pageHeaderSep: { color: PAGE_HEADER.mutedColor, fontSize: PAGE_HEADER.fontSize, margin: '0 2px' },

  // ─── A4 Page Footer ───
  pageFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 24,
    paddingTop: 8,
    borderTop: PAGE_FOOTER.borderTop,
    fontFamily: PAGE_FOOTER.fontFamily,
    fontSize: PAGE_FOOTER.fontSize,
  },
  pageFooterText: { color: PAGE_FOOTER.color, fontSize: PAGE_FOOTER.fontSize },

  // ─── Workflow bar (sticky bottom) ───
  workflowBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 12px',
    backgroundColor: '#EFF6FF',
    borderTop: '1px solid #DBEAFE',
    flexShrink: 0,
    flexWrap: 'wrap' as const,
  },

  workflowLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#374151',
  },

  workflowBtn: {
    padding: '3px 10px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    border: '1px solid',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap' as const,
  },

  // ─── Toast ───
  toast: {
    position: 'absolute' as const,
    bottom: 56,
    left: '50%',
    transform: 'translateX(-50%)',
    background: C.azul1,
    color: '#fff',
    padding: '6px 16px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    zIndex: 100,
    whiteSpace: 'nowrap' as const,
    animation: 'slideIn 0.2s ease',
  },

  // ─── Discard dialog ───
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(2px)',
  },

  discardDialog: {
    background: 'white',
    borderRadius: 10,
    padding: '20px 24px',
    width: 360,
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
};

export default DocumentPreviewPanel;
